import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { phappsRequest } from "../services/apiService";
import { DataGrid } from "@mui/x-data-grid";
import { Button } from "@mui/material";
import MUIDataGrid from "../components/MUIDataGrid";
import SFDateRangeSelector from "../components/SFDateRangeSelector";
import QudaLogo from "../Images/Quda_Logo.png";
import { API_PATHS } from "../config/apiConstants";
import StatBox from "../components/Statbox";
import ChartCard from "../components/ChartCard";
import { QUDA_COLORS } from "../config/colors";
import {
  ShoppingCart,
  TrendingUp,
  Person,
  Storefront,
  LocalShipping,
} from "@mui/icons-material";
import { QudaBoxTheme } from "../config/boxThemes";
import {
  Typography,
  Card,
  CardContent,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  ListItemButton,
} from "@mui/material";
import { Legend } from "recharts";
import SupplierPharmaciesModal from "../components/SupplierPharmaciesModal";

const QudaDash = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [chartData, setChartData] = useState([]);
  const [rawOrders, setRawOrders] = useState([]);
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  // Supplier Modal States
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierPharmacies, setSupplierPharmacies] = useState([]);
  const [loadingSupplierData, setLoadingSupplierData] = useState(false);
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);

  // Initialize dates
  useEffect(() => {
    const today = new Date();
    const fourWeeksAgo = new Date();
    fourWeeksAgo.setDate(today.getDate() - 28);
    setDateStart(fourWeeksAgo.toISOString().split("T")[0]);
    setDateEnd(today.toISOString().split("T")[0]);
  }, []);

  const preprocessOrders = (orders, dateStart, dateEnd) => {
    if (!orders || !Array.isArray(orders)) return [];
    const start = new Date(dateStart);
    const end = new Date(dateEnd);
    const dateMap = new Map();

    orders.forEach((o) => {
      const key = new Date(o.OrderDate).toISOString().split("T")[0];
      if (!dateMap.has(key)) {
        dateMap.set(key, {
          OrderDate: key,
          TotalOrders: 0,
          DispensaryOrders: 0,
          OTCOrders: 0
        });
      }
      const existing = dateMap.get(key);
      existing.TotalOrders += parseInt(o.TotalOrders || 0, 10);
      existing.DispensaryOrders += parseInt(o.DispensaryOrders || 0, 10);
      existing.OTCOrders += parseInt(o.OTCOrders || 0, 10);
      dateMap.set(key, existing);
    });

    const result = [];
    // Clone start date to avoid modifying it in the loop condition
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const key = d.toISOString().split("T")[0];
      result.push(
        dateMap.get(key) || {
          OrderDate: key,
          TotalOrders: 0,
          DispensaryOrders: 0,
          OTCOrders: 0,
        }
      );
    }
    return result;
  };

  const calculateStats = (ordersPerDay) => {
    const totalOrders = ordersPerDay.reduce(
      (sum, d) => sum + (d.TotalOrders || 0),
      0
    );
    const totalDays = ordersPerDay.length || 1;
    const averageDailyOrders = (totalOrders / totalDays).toFixed(2);
    return {
      totalOrders,
      averageDailyOrders,
    };
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const params = { DateStart: dateStart, DateEnd: dateEnd };
      const response = await phappsRequest(
        API_PATHS.QUDA_DASHBOARD,
        "GET",
        null,
        params
      );

      const orders = response.OrdersPerDay || [];
      setRawOrders(orders);
      const processed = preprocessOrders(orders, dateStart, dateEnd);
      setChartData(processed);
      const stats = calculateStats(processed);
      setData({
        ...response,
        stats,
      });
    } catch (err) {
      console.error("Error fetching Quda data:", err);
      setError("Failed to load data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (dateStart && dateEnd) {
      fetchData();
    }
  }, [dateStart, dateEnd]);

  const handleDateRangeChange = (newRange) => {
    if (newRange && newRange.length === 2) {
      setDateStart(newRange[0].toISOString().split("T")[0]);
      setDateEnd(newRange[1].toISOString().split("T")[0]);
    }
  };

  const handleSupplierClick = async (supplierName) => {
    setSelectedSupplier(supplierName);
    setIsSupplierModalOpen(true);
    setLoadingSupplierData(true);
    setSupplierPharmacies([]);

    try {
      const params = {
        SupplierName: supplierName,
        DateStart: dateStart,
        DateEnd: dateEnd,
      };

      const response = await phappsRequest(
        API_PATHS.SUPPLIER_PHARMACIES,
        "GET",
        null,
        params
      );
      
      if (response && response.SupplierPharmacies) {
         setSupplierPharmacies(Array.isArray(response.SupplierPharmacies) ? response.SupplierPharmacies : []);
      } else if (Array.isArray(response)) {
          setSupplierPharmacies(response);
      }

    } catch (error) {
      console.error("Failed to fetch supplier pharmacies:", error);
    } finally {
      setLoadingSupplierData(false);
    }
  };

  const handleCloseSupplierModal = () => {
    setIsSupplierModalOpen(false);
    setSelectedSupplier(null);
    setSupplierPharmacies([]);
  };

  const orderTableColumns = [
    {
      field: "chemistName",
      headerName: "Chemist",
      flex: 1,
      minWidth: 200,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "TotalOrders",
      headerName: "Total Orders",
      width: 150,
      align: "center",
      headerAlign: "center",
      valueFormatter: (value) =>
        Number(value || 0).toLocaleString(undefined, {
          maximumFractionDigits: 0,
        }),
    },
    {
      field: "DispensaryOrders",
      headerName: "Dispensary",
      width: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "OTCOrders",
      headerName: "OTC",
      width: 140,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "AvgOrdersPerDay",
      headerName: "Avg Orders/Day",
      width: 170,
      align: "center",
      headerAlign: "center",
      valueFormatter: (value) => Number(value || 0).toFixed(2),
    },
    {
      field: "actions",
      headerName: "Analytics",
      width: 120,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="contained"
          onClick={() => navigate(`/quda-analytics/${params.row.PHUUID}`)}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            boxShadow: 1,
            backgroundColor: QUDA_COLORS.TEAL_DARK,
            minWidth: 68,
            height: 32,
            lineHeight: 1,
            padding: "0 12px",
            "&:hover": {
              backgroundColor: QUDA_COLORS.TEAL_MID,
              boxShadow: 2,
            },
          }}
        >
          Open
        </Button>
      ),
    },
  ];

  const orderTableRows =
    data?.OverviewTable?.map((row, index) => ({
      id: row?.PHUUID ?? `${row?.chemistName || "row"}-${index}`,
      ...row,
    })) || [];

  // Pie Chart Data
  const pieData = [
    {
      x: "Dispensary",
      y: orderTableRows.reduce(
        (acc, row) => acc + (parseInt(row.DispensaryOrders) || 0),
        0
      ),
      text: "Dispensary",
      color: QUDA_COLORS.TEAL_DARK,
    },
    {
      x: "OTC",
      y: orderTableRows.reduce(
        (acc, row) => acc + (parseInt(row.OTCOrders) || 0),
        0
      ),
      text: "OTC",
      color: QUDA_COLORS.PURPLE_DARK,
    }, // Using 8C0F57 (PURPLE_DARK)
  ].filter((d) => d.y > 0);

  return (
    <div className="min-h-screen bg-white">
      <SupplierPharmaciesModal 
        open={isSupplierModalOpen} 
        onClose={handleCloseSupplierModal} 
        supplierName={selectedSupplier}
        pharmacies={supplierPharmacies}
        loading={loadingSupplierData}
      />
      
      <div className="p-4 space-y-6">
        {isLoading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading Quda data...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Top Row: Date Selector, Stats, Stacked Cards */}
            <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {/* Date Range Selector */}
              <div>
                <Box
                  sx={{
                    backgroundColor: QUDA_COLORS.TEAL_DARK,
                    borderRadius: "8px",
                    height: 140, // Match StatBox height
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    p: 2,
                    "& .text-black": { color: "white !important" },
                    "& .e-input-group input.e-input": {
                      color: "white !important",
                    },
                    "& .e-input-group.e-control-wrapper input.e-input": {
                      color: "white !important",
                    },
                    "& .e-input-group-icon": { color: "white !important" },
                  }}
                >
                  <SFDateRangeSelector
                    value={[new Date(dateStart), new Date(dateEnd)]}
                    onChange={handleDateRangeChange}
                  />
                </Box>
              </div>

              {/* Total Orders */}
              <div>
                <StatBox
                  theme={QudaBoxTheme}
                  logo={QudaLogo}
                  title="Total Orders"
                  value={data?.stats?.totalOrders || 0}
                  Icon={ShoppingCart}
                  valueFormatter={(v) => Number(v).toLocaleString()}
                />
              </div>

              {/* Avg Orders/Day */}
              <div>
                <StatBox
                  theme={QudaBoxTheme}
                  logo={QudaLogo}
                  title="Avg Orders/Day"
                  value={data?.stats?.averageDailyOrders || 0}
                  Icon={TrendingUp}
                  valueFormatter={(v) => Number(v).toFixed(2)}
                />
              </div>

              {/* Stacked Cards: Top Pharmacy & Top Staff */}
              <div>
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={1}
                  height={140}
                  justifyContent="space-between"
                >
                  {/* Top Pharmacy */}
                  <Box
                    flex={1}
                    bgcolor={QUDA_COLORS.PURPLE_DARK}
                    borderRadius="8px"
                    p={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                    sx={{ minHeight: 0, overflow: "hidden" }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: "white", opacity: 0.8 }}
                        noWrap
                      >
                        Top Pharmacy
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="white"
                        noWrap
                      >
                        {data?.TopChemist?.chemistName || "N/A"}
                      </Typography>
                    </Box>
                    <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color="white"
                        sx={{ lineHeight: 1 }}
                      >
                        {data?.TopChemist?.TotalOrders ?? "N/A"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="white"
                        sx={{ opacity: 0.8 }}
                      >
                        Orders
                      </Typography>
                    </Box>
                  </Box>

                  {/* Top Staff */}
                  <Box
                    flex={1}
                    bgcolor={QUDA_COLORS.PURPLE_DARK}
                    borderRadius="8px"
                    p={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                    sx={{ minHeight: 0 }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: "white", opacity: 0.8 }}
                        noWrap
                      >
                        Top Staff
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="white"
                        noWrap
                      >
                        {data?.TopStaffMember?.StaffName || "N/A"}
                      </Typography>
                    </Box>
                    <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color="white"
                        sx={{ lineHeight: 1 }}
                      >
                        {data?.TopStaffMember?.interactionCount ?? "N/A"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="white"
                        sx={{ opacity: 0.8 }}
                      >
                        Interactions
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </div>
            </section>

            {/* Middle Row: Orders Per Day Graph */}
            <section className="grid grid-cols-1 gap-6">
              {/* Orders Per Day Graph */}
              <div>
                <ChartCard
                  title="Orders Per Day"
                  data={chartData}
                  primaryXAxis={{
                    valueType: "DateTime",
                    labelFormat: "dd/MM",
                    intervalType: "Days",
                    edgeLabelPlacement: "Shift",
                    majorGridLines: { width: 0 },
                  }}
                  primaryYAxis={{
                    labelFormat: "{value}",
                    title: "Orders",
                    minimum: 0,
                    majorGridLines: { width: 0 },
                  }}
                  series={[
                    {
                      dataSource: chartData,
                      xName: "OrderDate",
                      yName: "DispensaryOrders",
                      name: "Dispensary Orders",
                      type: "StackingColumn",
                      fill: QUDA_COLORS.TEAL_DARK,
                      cornerRadius: { topLeft: 4, topRight: 4 },
                    },
                    {
                      dataSource: chartData,
                      xName: "OrderDate",
                      yName: "OTCOrders",
                      name: "OTC Orders",
                      type: "StackingColumn",
                      fill: QUDA_COLORS.PURPLE_DARK,
                      cornerRadius: { topLeft: 4, topRight: 4 },
                    },
                  ]}
                  pieData={pieData}
                  pieTitle="Order Types"
                />
              </div>

            </section>

            {/* Bottom Row: Suppliers and Table */}
            <section className="grid grid-cols-1 xl:grid-cols-4 gap-6 mt-6">
              {/* Chemist Orders Table */}
              <div className="xl:col-span-3">
                <Box
                  sx={{
                    border: `2px solid ${QUDA_COLORS.TEAL_DARK}`,
                    borderRadius: "8px",
                    overflow: "hidden",
                    height: "480px",
                  }}
                >
                  <Card sx={{ height: "100%", boxShadow: "none" }}>
                    <CardContent sx={{ height: "100%", p: 0 }}>
                      <Box className="flex items-center space-x-2 p-4 pb-0">
                        <ShoppingCart sx={{ color: QUDA_COLORS.TEAL_DARK }} />
                        <Typography
                          variant="h6"
                          className="font-bold"
                          sx={{ color: QUDA_COLORS.TEAL_DARK }}
                        >
                          Quda Chemist Orders
                        </Typography>
                      </Box>
                      <Divider
                        sx={{ m: 2, borderColor: QUDA_COLORS.TEAL_LIGHT }}
                      />
                      <div style={{ height: "calc(100% - 80px)", width: "100%", padding: "0 16px 16px 16px" }}>
                        <DataGrid
                          rows={orderTableRows}
                          columns={orderTableColumns}
                          pageSize={5}
                          rowsPerPageOptions={[5]}
                          disableSelectionOnClick
                          sx={{
                            border: "none",
                            "& .MuiDataGrid-columnHeaders": {
                              backgroundColor: `${QUDA_COLORS.TEAL_DARK} !important`,
                              color: "white !important",
                              fontWeight: "bold",
                              borderBottom: "none",
                            },
                            "& .MuiDataGrid-columnHeader": {
                              backgroundColor: `${QUDA_COLORS.TEAL_DARK} !important`,
                              color: "white !important",
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                              fontWeight: "bold",
                              color: "white !important",
                            },
                            "& .MuiDataGrid-columnSeparator": {
                              color: "white !important",
                            },
                            "& .MuiDataGrid-menuIconButton": {
                              color: "white !important",
                            },
                            "& .MuiDataGrid-sortIcon": {
                              color: "white !important",
                            },
                            "& .MuiDataGrid-cell:focus": {
                              outline: "none",
                            },
                          }}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </Box>
              </div>

              {/* Top Suppliers */}
              <div className="xl:col-span-1">
                <Box
                  sx={{
                    border: `2px solid ${QUDA_COLORS.TEAL_DARK}`,
                    borderRadius: "8px",
                    overflow: "hidden",
                    height: "480px", // Match estimated table height
                  }}
                >
                  <Card sx={{ height: "100%", boxShadow: "none" }}>
                    <CardContent sx={{ height: "100%" }}>
                      <Box className="flex items-center space-x-2 mb-4">
                        <LocalShipping sx={{ color: QUDA_COLORS.TEAL_DARK }} />
                        <Typography
                          variant="h6"
                          className="font-bold"
                          sx={{ color: QUDA_COLORS.TEAL_DARK }}
                        >
                          Top Suppliers
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{ fontStyle: "italic", display: "block", mb: 1, mt: -1 }}
                      >
                        Select a supplier to view pharmacies
                      </Typography>
                      <Divider
                        sx={{ mb: 2, borderColor: QUDA_COLORS.TEAL_LIGHT }}
                      />
                      {data?.TopSuppliers && data.TopSuppliers.length > 0 ? (
                        <List sx={{ overflow: 'auto', height: 'calc(100% - 80px)' }}>
                          {data.TopSuppliers.map((item, index) => (
                            <ListItem key={index} className="px-0" disablePadding>
                              <ListItemButton
                                onClick={() => handleSupplierClick(item.supplier)}
                                sx={{
                                  borderRadius: "8px",
                                  "&:hover": {
                                    backgroundColor: QUDA_COLORS.TEAL_LIGHT + "40",
                                  },
                                }}
                              >
                              <ListItemAvatar>
                                <Avatar
                                  sx={{
                                    backgroundColor: QUDA_COLORS.TEAL_LIGHT,
                                    color: QUDA_COLORS.TEAL_DARK,
                                    fontWeight: "bold",
                                  }}
                                >
                                  {index + 1}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={item.supplier}
                                secondary={`${item.orderCount} orders`}
                                primaryTypographyProps={{ fontWeight: "medium" }}
                              />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          height="80%"
                        >
                          <Typography color="textSecondary">
                            No supplier data available
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default QudaDash;
